/**
 * Tool icon utility.
 *
 * Maps tool icon names from the registry to Lucide React icon components.
 */

import {
  Mail,
  MessageSquare,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  Users,
  Settings,
  Search,
  Edit,
  Trash,
  Plus,
  Minus,
  X,
  Check,
  AlertTriangle,
  Info,
  HelpCircle,
  Send,
  Reply,
  Forward,
  Archive,
  Bookmark,
  Star,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Flag,
  Bell,
  Calendar,
  Folder,
  File,
  Image,
  Link,
  Code,
  Terminal,
  Database,
  Cloud,
  Server,
  Wifi,
  Lock,
  Unlock,
  Key,
  Shield,
  Eye,
  EyeOff,
  Play,
  Pause,
  Square,
  Volume2,
  Download,
  Upload,
  Share,
  Copy,
  RefreshCw,
  RotateCw,
  ZoomIn,
  ZoomOut,
  Maximize,
  Minimize,
  ExternalLink,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  MoreVertical,
  Grid,
  List,
  Layers,
  Map,
  Navigation,
  Compass,
  Home,
  Building,
  Store,
  ShoppingCart,
  CreditCard,
  DollarSign,
  Percent,
  TrendingUp,
  TrendingDown,
  BarChart,
  PieChart,
  Activity,
  Zap,
  Wrench,
  Package,
  Box,
  Gift,
  Award,
  Trophy,
  Target,
  Crosshair,
  Sun,
  Moon,
  Cloudy,
  Coffee,
  Utensils,
  Camera,
  Video,
  Music,
  Tv,
  Monitor,
  Smartphone,
  Tablet,
  Laptop,
  HardDrive,
  Cpu,
  Bluetooth,
  Globe,
  MapPin,
  Phone,
  PhoneCall,
  type LucideIcon,
} from 'lucide-react'

/**
 * Icon map from tool registry names to Lucide components.
 */
const iconMap: Record<string, LucideIcon> = {
  // Email/Communication
  mail: Mail,
  email: Mail,
  message: MessageSquare,
  chat: MessageSquare,
  send: Send,
  reply: Reply,
  forward: Forward,

  // Document/File
  file: File,
  document: FileText,
  folder: Folder,
  archive: Archive,
  image: Image,

  // Status
  alert: AlertCircle,
  warning: AlertTriangle,
  error: AlertCircle,
  success: CheckCircle,
  check: Check,
  info: Info,
  help: HelpCircle,
  question: HelpCircle,

  // User/People
  user: User,
  users: Users,
  person: User,
  people: Users,
  team: Users,

  // Actions
  edit: Edit,
  delete: Trash,
  remove: Trash,
  add: Plus,
  plus: Plus,
  minus: Minus,
  close: X,
  search: Search,
  settings: Settings,
  config: Settings,

  // Time/Calendar
  clock: Clock,
  time: Clock,
  calendar: Calendar,
  schedule: Calendar,

  // Bookmarks/Stars
  bookmark: Bookmark,
  star: Star,
  favorite: Star,
  heart: Heart,
  like: ThumbsUp,
  dislike: ThumbsDown,
  flag: Flag,

  // Notifications
  bell: Bell,
  notification: Bell,

  // Links/External
  link: Link,
  external: ExternalLink,
  url: Link,

  // Code/Technical
  code: Code,
  terminal: Terminal,
  database: Database,
  server: Server,
  cloud: Cloud,
  api: Terminal,

  // Security
  lock: Lock,
  unlock: Unlock,
  key: Key,
  shield: Shield,
  security: Shield,

  // View
  eye: Eye,
  view: Eye,
  hidden: EyeOff,
  invisible: EyeOff,

  // Media
  play: Play,
  pause: Pause,
  stop: Square,
  video: Video,
  camera: Camera,
  music: Music,
  audio: Volume2,

  // Transfer
  download: Download,
  upload: Upload,
  share: Share,
  copy: Copy,

  // Refresh/Sync
  refresh: RefreshCw,
  sync: RotateCw,
  reload: RefreshCw,

  // Zoom
  'zoom-in': ZoomIn,
  'zoom-out': ZoomOut,

  // Window
  maximize: Maximize,
  minimize: Minimize,
  fullscreen: Maximize,

  // Navigation
  home: Home,
  building: Building,
  store: Store,
  map: Map,
  location: MapPin,
  pin: MapPin,
  compass: Compass,
  navigation: Navigation,

  // Commerce
  cart: ShoppingCart,
  shopping: ShoppingCart,
  payment: CreditCard,
  card: CreditCard,
  money: DollarSign,
  dollar: DollarSign,
  percent: Percent,

  // Charts/Analytics
  chart: BarChart,
  'bar-chart': BarChart,
  'pie-chart': PieChart,
  trending: TrendingUp,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  activity: Activity,
  analytics: Activity,

  // Tools
  wrench: Wrench,
  tool: Wrench,
  tools: Wrench,
  package: Package,
  box: Box,

  // Awards
  award: Award,
  trophy: Trophy,
  gift: Gift,
  target: Target,
  crosshair: Crosshair,

  // Weather
  sun: Sun,
  moon: Moon,
  cloudy: Cloudy,
  weather: Sun,

  // Food/Drink
  coffee: Coffee,
  food: Utensils,
  restaurant: Utensils,

  // Devices
  phone: Phone,
  call: PhoneCall,
  smartphone: Smartphone,
  tablet: Tablet,
  laptop: Laptop,
  desktop: Monitor,
  tv: Tv,

  // Hardware
  cpu: Cpu,
  memory: HardDrive,
  storage: HardDrive,
  bluetooth: Bluetooth,
  wifi: Wifi,

  // Layout
  grid: Grid,
  list: List,
  layers: Layers,

  // Arrows
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,

  // Chevrons
  'chevron-up': ChevronUp,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,

  // More
  more: MoreHorizontal,
  'more-horizontal': MoreHorizontal,
  'more-vertical': MoreVertical,
  dots: MoreHorizontal,

  // Globe
  globe: Globe,
  world: Globe,
  web: Globe,
  internet: Globe,

  // Zap/Energy
  zap: Zap,
  bolt: Zap,
  lightning: Zap,
  energy: Zap,
}

/**
 * Get a Lucide icon component by name.
 *
 * Returns a fallback icon if the name is not found.
 */
export function getToolIcon(name: string | null | undefined): LucideIcon {
  if (!name) {
    return HelpCircle
  }

  const normalizedName = name.toLowerCase().replace(/[_\s]+/g, '-')
  return iconMap[normalizedName] ?? HelpCircle
}

export default getToolIcon
